import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { useParams } from 'react-router-dom';
import { lookupErrorMessage, lookupQrSlug } from '../lib/qr';
import { AssetDetailPage } from './AssetDetailPage';
import { ToteDetailPage } from './ToteDetailPage';

export function QrLandingPage(): JSX.Element {
  const params = useParams();
  const slug = params.slug ?? '';
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'tote'; number: string }
    | { status: 'asset'; serial: string }
    | { status: 'missing'; message: string }
  >({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const found = await lookupQrSlug(slug);
        if (cancelled) return;
        if (found?.kind === 'tote') setState({ status: 'tote', number: found.tote.number });
        else if (found?.kind === 'asset') setState({ status: 'asset', serial: found.asset.serial });
        else setState({ status: 'missing', message: 'No tote or asset matches that QR.' });
      } catch (error: unknown) {
        if (!cancelled) {
          setState({
            status: 'missing',
            message: lookupErrorMessage(error, 'Could not look up that QR.'),
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (state.status === 'tote') {
    return <ToteDetailPage qrLanding number={state.number} />;
  }
  if (state.status === 'asset') {
    return <AssetDetailPage qrLanding serial={state.serial} />;
  }

  return (
    <section className="card" data-testid="qr-landing">
      {state.status === 'loading' ? <p className="meta">Looking up QR…</p> : null}
      {state.status === 'missing' ? (
        <p className="status status--error" data-testid="qr-landing-error">
          {state.message}
        </p>
      ) : null}
    </section>
  );
}
